import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import SimpleCombobox from "../../components/SimpleCombobox";
import api from "../../api";
import { supplyTypeLabels } from "./SuppliesPage";

export function SupplyDetailPage() {
    const { idSupply } = useParams();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [supply, setSupply] = useState(null);

    useEffect(() => {
        Promise.all([
            api.supplies.getOne(idSupply),
            api.categories.getMulti(),
            api.brands.getMulti(),
        ]).then(([supplyResponse, categoriesResponse, brandsResponse]) => {
            setCategories(categoriesResponse);
            setBrands(brandsResponse);
            setSupply({
                ...supplyResponse,
                category: categoriesResponse.find(
                    (c) => c.name === supplyResponse.category
                )?.id,
                brand: brandsResponse.find(
                    (b) => b.name === supplyResponse.brand
                )?.id,
            });
            setLoading(false);
        });
    }, [idSupply]);

    const handleChange = (field, value) => {
        const parsedValue =
            typeof value === "object" && value !== null ? value.id : value;
        setSupply((prev) => ({ ...prev, [field]: parsedValue }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            const { orStock, ...editable } = supply;
            await api.supplies.update(idSupply, editable, true);
            navigate("/supplies");
        } catch (e) {
            setError(e.detail || "Hubo un error al guardar los cambios");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-gray-100 h-screen">
            <Sidebar
                currentScreen={"Insumos"}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
            />
            <div className="md:pl-64 flex flex-col h-screen flex-1">
                <Header setSidebarOpen={setSidebarOpen} />
                <main className="flex-1 space-y-6 bg-gray-100 py-8 px-6 lg:px-8">
                    <h2 className="text-gray-600 text-4xl font-bold">
                        Editar insumo
                    </h2>
                    {loading ? (
                        <div className="flex h-full items-center justify-center ">
                            <div className="w-16 h-16 border-b-2 border-blue-900 rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            className="bg-white rounded-lg shadow p-6 max-w-xl grid grid-cols-2 gap-4"
                        >
                            <SimpleCombobox
                                inputClassName="relative"
                                placeholder={"Escribe el nombre..."}
                                handleChange={handleChange}
                                name="category"
                                label="Nombre del insumo"
                                className="shadow-sm pl-2 mt-1 w-full bg-white border border-gray-300 focus:ring-primary focus:border-primary block sm:text-sm rounded-md"
                                options={categories}
                                value={categories.find(
                                    (c) => c.id === supply.category
                                )}
                                required={true}
                            />
                            <SimpleCombobox
                                inputClassName="relative"
                                placeholder={"Seleccione la marca"}
                                handleChange={handleChange}
                                name="brand"
                                label="Marca"
                                className="shadow-sm pl-2 mt-1 w-full bg-white border border-gray-300 focus:ring-primary focus:border-primary block sm:text-sm rounded-md"
                                options={brands}
                                value={brands.find((b) => b.id === supply.brand)}
                                required={true}
                            />
                            <div>
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    Tipo de insumo
                                </label>
                                <select
                                    value={supply.supplyType}
                                    onChange={(e) =>
                                        handleChange("supplyType", e.target.value)
                                    }
                                    className="shadow-sm pl-2 mt-1 w-full bg-white border border-gray-300 focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                                >
                                    {Object.entries(supplyTypeLabels).map(
                                        ([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                            {["reusable", "resterilizable"].includes(
                                supply.supplyType
                            ) && (
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        Usos máximos esperados
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Opcional"
                                        value={supply.maxUses || ""}
                                        onChange={(e) =>
                                            handleChange(
                                                "maxUses",
                                                e.target.value
                                            )
                                        }
                                        className="shadow-sm pl-2 mt-1 w-full bg-white border border-gray-300 focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    Stock mínimo
                                </label>
                                <input
                                    type="number"
                                    value={supply.minStock}
                                    onChange={(e) =>
                                        handleChange("minStock", e.target.value)
                                    }
                                    className="shadow-sm pl-2 mt-1 w-full bg-white border border-gray-300 focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    Stock total
                                </label>
                                <input
                                    type="number"
                                    value={supply.stock}
                                    onChange={(e) =>
                                        handleChange("stock", e.target.value)
                                    }
                                    className="shadow-sm pl-2 mt-1 w-full bg-white border border-gray-300 focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    Stock quirófano
                                </label>
                                <input
                                    type="number"
                                    value={supply.orStock}
                                    disabled
                                    className="shadow-sm pl-2 mt-1 w-full bg-gray-100 border border-gray-300 sm:text-sm rounded-md"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Se modifica desde "Mover stock", no acá.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    Fecha de vencimiento
                                </label>
                                <input
                                    type="date"
                                    value={supply.expirationDate}
                                    onChange={(e) =>
                                        handleChange(
                                            "expirationDate",
                                            e.target.value
                                        )
                                    }
                                    className="shadow-sm pl-2 mt-1 w-full bg-white border border-gray-300 focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                    Costo unitario
                                </label>
                                <input
                                    type="number"
                                    step="any"
                                    value={supply.unitCost}
                                    onChange={(e) =>
                                        handleChange("unitCost", e.target.value)
                                    }
                                    className="shadow-sm pl-2 mt-1 w-full bg-white border border-gray-300 focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                                />
                            </div>
                            {error && (
                                <p className="col-span-2 text-sm text-red-600">
                                    {error}
                                </p>
                            )}
                            <div className="col-span-2 flex gap-3">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary disabled:opacity-50"
                                >
                                    {saving ? "Guardando..." : "Guardar cambios"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate("/supplies")}
                                    className="flex-1 flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-semibold rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </form>
                    )}
                </main>
            </div>
        </div>
    );
}
